import React, { useState, useEffect } from 'react';
import { Button } from '@/src/shadComponents/ui/button';
import { Badge } from '@/src/shadComponents/ui/badge';
import Loading from '@/src/Common/components/Loading';
import Table, { createColumnHelper } from '../../Common/components/Table';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';
import {
    useDeviceRegistrationAutoenroll,
    DeviceRegistrationAutoenroll,
    CreateDeviceRegistrationRequest,
} from './hooks/useDeviceRegistrationAutoenroll';
import CsvImportModal from './CsvImportModal';
import DeviceRegistrationCreateModal from './DeviceRegistrationCreateModal';
import DeviceRegistrationActions from './DeviceRegistrationActions';
import { toast } from 'sonner';
import moment from 'moment-timezone';
import useRerender from '../../Common/components/hooks/useRerender';

const columnHelper = createColumnHelper<DeviceRegistrationAutoenroll>();

export default function AutoEnrollment() {
    const { orgId } = useUserOrg();
    const {
        listDeviceRegistrations,
        createDeviceRegistrations,
        createSingleDeviceRegistration,
        isLoading,
    } = useDeviceRegistrationAutoenroll(orgId);
    const [deviceRegistrations, setDeviceRegistrations] = useState<
        DeviceRegistrationAutoenroll[]
    >([]);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const { render, renderCount } = useRerender();

    const loadDeviceRegistrations = async () => {
        setIsRefreshing(true);
        try {
            const registrations = await listDeviceRegistrations();
            setDeviceRegistrations(registrations);
        } catch (error) {
            toast.error('Failed to load device registrations');
            console.error('Error loading device registrations:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        loadDeviceRegistrations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, renderCount]);

    const handleImport = async (devices: CreateDeviceRegistrationRequest[]) => {
        try {
            await createDeviceRegistrations(devices);
            toast.success(
                `Successfully imported ${devices.length} device${
                    devices.length !== 1 ? 's' : ''
                }`,
            );
            await loadDeviceRegistrations(); // Refresh the list
        } catch (error) {
            toast.error('Failed to import devices');
            throw error; // Re-throw to let modal handle the error
        }
    };

    const onDelete = () => {
        toast.success('Device registration deleted');
        render(); // Refresh the list
    };

    const handleCreateSingle = async (
        device: CreateDeviceRegistrationRequest,
    ) => {
        try {
            await createSingleDeviceRegistration(device);
            toast.success(
                `Successfully created device registration for "${device.deviceName}"`,
            );
        } catch (error) {
            toast.error('Failed to create device registration');
            throw error; // Re-throw to let modal handle the error
        }
    };

    const onDeviceCreated = () => {
        render(); // Refresh the list
    };

    const columnDefinitions = [
        columnHelper.accessor('deviceId', {
            id: 'deviceId',
            header: () => <span>Serial Number</span>,
            cell: (info) => (
                <span className="font-mono text-sm">{info.getValue()}</span>
            ),
        }),
        columnHelper.accessor('deviceName', {
            id: 'deviceName',
            header: () => <span>Device Name</span>,
            cell: (info) => (
                <span className="font-medium">{info.getValue()}</span>
            ),
        }),
        columnHelper.accessor('groupNames', {
            id: 'groupNames',
            header: () => <span>Groups</span>,
            cell: (info) => {
                const groups = info.getValue();
                if (!groups || groups.trim() === '') {
                    return <span className="text-gray-400 italic">None</span>;
                }
                const groupList = groups
                    .split(',')
                    .map((g) => g.trim())
                    .filter((g) => g !== '');
                return (
                    <div className="flex flex-wrap gap-1">
                        {groupList.map((group, index) => (
                            <Badge
                                key={index}
                                variant="secondary"
                                className="text-xs"
                            >
                                {group}
                            </Badge>
                        ))}
                    </div>
                );
            },
        }),
        columnHelper.accessor('counter', {
            id: 'counter',
            header: () => <span>Usage Count</span>,
            cell: (info) => (
                <span className="text-sm">
                    {info.getValue()} time{info.getValue() !== 1 ? 's' : ''}
                </span>
            ),
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: () => <span>Created</span>,
            cell: (info) => (
                <span className="text-sm text-gray-500">
                    {moment(info.getValue()).format('MMM D, YYYY')}
                </span>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            header: () => <span>Actions</span>,
            cell: (props: { row: any }) => (
                <DeviceRegistrationActions
                    orgId={orgId}
                    row={props.row}
                    onDelete={onDelete}
                />
            ),
        }),
    ];

    if (isLoading && deviceRegistrations.length === 0) {
        return <Loading isLoading={true} />;
    }

    return (
        <div>
            <div className="row flex mx-[20px] mb-6">
                <div className="flex-1">
                    <h2 className="text-2xl font-semibold leading-6 text-gray-900">
                        Auto Enrollment
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Manage device pre-registration for automatic enrollment.
                        Devices can use their registered serial number to
                        automatically obtain credentials and be assigned to
                        groups.
                        <br />
                        <br />
                        <strong>
                            For all iOS apps, the device must be managed and
                            configured with the `DeviceSerialNumber` key set in
                            the `com.apple.configuration.managed` profile.
                        </strong>
                    </p>
                </div>
                <div className="flex items-center space-x-4">
                    <Button
                        variant="outline"
                        onClick={loadDeviceRegistrations}
                        disabled={isRefreshing}
                        className="flex items-center"
                    >
                        <svg
                            className={`w-4 h-4 mr-2 ${
                                isRefreshing ? 'animate-spin' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                        </svg>
                        Refresh
                    </Button>
                    <Button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="Primary-button"
                    >
                        Add Device
                    </Button>
                    <Button
                        onClick={() => setIsImportModalOpen(true)}
                        className="Primary-button"
                    >
                        Import CSV
                    </Button>
                </div>
            </div>

            {deviceRegistrations.length === 0 ? (
                <div className="text-center py-12">
                    <div className="mx-auto max-w-md">
                        <svg
                            className="mx-auto h-12 w-12 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">
                            No devices registered
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Get started by adding a single device or importing
                            multiple devices from a CSV file.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="bg-white shadow rounded-lg">
                    <Table
                        data={deviceRegistrations}
                        columns={columnDefinitions}
                    />
                </div>
            )}

            <DeviceRegistrationCreateModal
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}
                onDeviceCreated={onDeviceCreated}
                onCreateDevice={handleCreateSingle}
            />

            <CsvImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onImport={handleImport}
                existingDevices={deviceRegistrations}
            />
        </div>
    );
}
